import { useCallback, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ContractsSmsBulkPreviewQuery,
  SendContractsSmsBulkMutation,
  useContractsSmsBulkPreviewQuery,
  useSendContractsSmsBulkMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useDebouncedValue } from '../Meta/useDebouncedValue';
import {
  ESIGN_STUDENT_KIND,
  isEsignLinkReady,
  isEsignSigned,
} from './esignModel';
import { newSmsRequestKey, SmsSendBlock } from '../Sms/smsModel';
import { useToasts } from '../Toast/useToasts';
import { SMS_PREVIEW_DEBOUNCE_MS } from './useContractSms';
import { IEsignFieldError, toEsignErrors } from './useEsignMutations';
import { useEsignPermissions } from './useEsignPermissions';

/**
 * ТЗ 0083 — одна SMS многим подписантам из списка договоров.
 *
 * Уходит договорам, которые ждут подписи или уже подписаны, и чью переписку
 * ведёт сотрудник; остальным — строка с причиной. Подписанным — только без
 * `{link}`: ссылки на подпись у них больше нет. Предпросмотр и отправка
 * проверяют одной функцией на сервере: «уйдёт 12» в окне и 12 ушедших не
 * расходятся.
 */

/** Сервер не берёт больше за раз — это страница списка, а не выгрузка. */
export const CONTRACTS_SMS_BULK_MAX = 200;

/**
 * Причины «не уйдёт» (`ContractSmsBulkRowType.code`) — переводятся
 * `esign.bulkSms.reasons.<CODE>`; неизвестный код показывается текстом
 * сервера. Список — для локаль-гейта: новый код без перевода уронит его.
 */
export const CONTRACTS_SMS_BULK_REASONS = [
  'NOT_FOUND',
  'NOT_STUDENT',
  'NOT_YOUR_CONTRACT',
  'NOT_SENT',
  'SIGNED',
  'CANCELLED',
  'EXPIRED',
  'NO_PHONE',
  'FOREIGN_NUMBER',
  'OPTED_OUT',
  'STOPPED',
  'DAILY_CAP',
  'COOLDOWN',
  'TOO_LONG',
  'DISABLED_ON_SERVER',
  'DISABLED',
  'ENTITY_NOT_ALLOWED',
  'NOT_ALLOWLISTED',
  'ALREADY_SENT',
  'WRONG_STATUS',
  'SMS_DISABLED',
  // Проверка текста шаблона (`outreach/services/message_templates.py`):
  // окно обычно ловит их само и не спрашивает сервер, но ответ бывает.
  'UNKNOWN_PLACEHOLDER',
  'REQUIRED',
  'LINK_IN_SUBJECT',
  // Перепроверка 0084: SMS по договору уходит со ссылкой на подпись —
  // сократителя нет, отказ до очереди; нет включённого текста.
  'SHORTLINK_DISABLED',
  'NO_TEMPLATE',
  // Подписанному договору — `{link}` в тексте: ссылки на подпись нет.
  'LINK_NOT_READY',
] as const;

/** Почему строку списка нельзя выбрать для массовой SMS. */
export const CONTRACTS_SMS_BULK_ROW_BLOCKS = [
  'notStudent',
  'notAwaiting',
  'notYours',
] as const;
export type ContractsSmsBulkRowBlock = typeof CONTRACTS_SMS_BULK_ROW_BLOCKS[number];

/**
 * Строка годится для массовой SMS: договор студента, ждёт подписи или уже
 * подписан, и сотрудник ведёт его переписку (ТЗ 0128: любой продавец).
 * Подписанному SMS уйдёт только без
 * `{link}` — это решает текст, а не строка (окно предупреждает). Сервер
 * проверит ещё раз (номер, отписка, лимит) — здесь отсекается то, что видно
 * по строке, чтобы флажок не обещал зря.
 */
export const contractsSmsBulkRowBlock = (row: {
  kind: string;
  status: string;
  canManage: boolean;
  /** ТЗ 0128: SMS — переписка; её ведёт и любой продавец. */
  canSeeCorrespondence?: boolean | null;
  signedAt?: unknown;
  cancelConflict?: boolean | null;
}): ContractsSmsBulkRowBlock | null => {
  if (row.kind !== ESIGN_STUDENT_KIND) return 'notStudent';
  if (!isEsignLinkReady(row.status) && !isEsignSigned(row)) {
    return 'notAwaiting';
  }
  if (!(row.canSeeCorrespondence ?? row.canManage)) return 'notYours';
  return null;
};

export interface IContractsSmsBulkCheck {
  selected: number;
  text: string;
  unknownCount: number;
  pending: boolean;
  failed: boolean;
  /** Ответ проверки: `null` — ещё не было. */
  willSend: number | null;
  tooLong: boolean;
}

/**
 * Почему «Отправить» в окне массовой SMS серая — одна причина, самая ранняя.
 * Порядок: выбор → текст → проверка → кому уйдёт → длина.
 */
export const contractsSmsBulkBlock = (
  check: IContractsSmsBulkCheck,
): SmsSendBlock | null => {
  if (check.selected > CONTRACTS_SMS_BULK_MAX) return 'tooMany';
  if (check.selected === 0) return 'nobody';
  if (!check.text.trim()) return 'empty';
  if (check.unknownCount > 0) return 'unknownPlaceholder';
  if (check.failed) return 'previewFailed';
  if (check.pending || check.willSend === null) return 'previewing';
  if (check.willSend === 0) return 'noneEligible';
  if (check.tooLong) return 'tooLong';
  return null;
};

export type IContractsSmsBulkPreview = NonNullable<
  ContractsSmsBulkPreviewQuery['previewContractsSms']
>;
export type IContractsSmsBulkRow = IContractsSmsBulkPreview['rows'][number];
type IBulkSendPayload = NonNullable<
  SendContractsSmsBulkMutation['sendContractsSms']
>;

export interface IUseContractsSmsBulkPreview {
  preview: IContractsSmsBulkPreview | null;
  pending: boolean;
  /** Последняя проверка не удалась (даже если на экране прошлый ответ). */
  failed: boolean;
  /** На экране прошлый ответ: новый не пришёл из-за ошибки. */
  stale: boolean;
  reload: () => void;
}

/**
 * Предпросмотр: сколько уйдёт, кому нет и почему, образец текста. Пустой
 * текст — тоже запрос (причины пропуска видны до того, как начали писать):
 * сервер на пустом тексте только отсеивает договоры.
 */
export const useContractsSmsBulkPreview = (
  contractIds: string[],
  text: string,
  enabled: boolean,
): IUseContractsSmsBulkPreview => {
  const { canWriteStudent } = useEsignPermissions();
  const trimmed = text.trim();
  const debounced = useDebouncedValue(trimmed, SMS_PREVIEW_DEBOUNCE_MS);
  const skip =
    !enabled ||
    !canWriteStudent ||
    contractIds.length === 0 ||
    contractIds.length > CONTRACTS_SMS_BULK_MAX;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useContractsSmsBulkPreviewQuery({
    variables: { contractIds, text: debounced },
    skip,
    // Причины меняются от минуты к минуте (кулдаун, дневной лимит): тот же
    // текст после отправки обязан пересчитаться, а не взяться из кэша.
    fetchPolicy: 'network-only',
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  const current = (data ?? previousData)?.previewContractsSms ?? null;
  const failed = !skip && Boolean(error);
  return {
    preview: current,
    pending: !failed && (debounced !== trimmed || loading),
    failed,
    stale: failed && Boolean(current),
    reload,
  };
};

export interface IContractsSmsBulkResult {
  ok: boolean;
  sent: number;
  skipped: number;
  rows: IBulkSendPayload['rows'];
  errors: IEsignFieldError[];
}

/**
 * Отправка. Ключ запроса живёт до успешной отправки: повтор после обрыва
 * сети — тот же ключ, и сервер не шлёт второй раз тем, кому уже ушло.
 */
export const useContractsSmsBulkSend = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [mutate, { loading: sending }] = useSendContractsSmsBulkMutation();
  const inFlightRef = useRef(false);
  const keyRef = useRef(newSmsRequestKey());

  const send = useCallback(
    async (
      contractIds: string[],
      text: string,
      templateId: string | null,
    ): Promise<IContractsSmsBulkResult> => {
      const empty = { ok: false, sent: 0, skipped: 0, rows: [], errors: [] };
      if (inFlightRef.current) return empty;
      inFlightRef.current = true;
      try {
        const { data } = await mutate({
          variables: {
            contractIds,
            text: text.trim(),
            templateId,
            requestKey: keyRef.current,
          },
          // Колонку «SMS» списка не перечитываем по имени: на странице без
          // отправленных договоров её запрос пропущен, и перезапрос ушёл бы
          // без переменных (400 → /500). SMS встают в очередь, колонка
          // считает УШЕДШИЕ и сама перечитывается раз в минуту.
        });
        const payload = data?.sendContractsSms;
        const errors = toEsignErrors(payload?.customErrors);
        if (payload?.success) {
          keyRef.current = newSmsRequestKey();
          return {
            ok: true,
            sent: payload.sent ?? 0,
            skipped: payload.skipped ?? 0,
            rows: payload.rows ?? [],
            errors,
          };
        }
        return { ...empty, errors };
      } catch (caught) {
        const noAccess =
          caught instanceof ApolloError && caught.graphQLErrors.length > 0;
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('esign.sms.toast.failed'),
            text: t(
              noAccess
                ? 'esign.sms.errors.NO_ACCESS'
                : 'esign.sms.errors.NETWORK',
            ),
          },
          type: GlobalToastTypeNames.Esign,
        });
        return empty;
      } finally {
        inFlightRef.current = false;
      }
    },
    [addToast, mutate, t],
  );

  return { send, sending };
};
